/**
 * The admin KPI dashboard's building blocks (A-02): cards, the period / sort pickers, stat tiles,
 * change and rank-move badges, share bars and loading / error states.
 *
 * Colour comes from the tokens in src/index.css. A change or a rank move is never colour alone:
 * it carries an arrow and its words (with a fuller reading for screen readers).
 */

import { useRef, type KeyboardEvent, type ReactNode } from "react";

import { changeLabel, rankMove, tone, type Change } from "../../lib/kpi";

export function Card({
  title,
  subtitle,
  action,
  children,
  className = "",
  bodyClassName = "",
  busy = false,
}: {
  title: string;
  subtitle?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
  /** e.g. "flex flex-col", for a chart that fills the card. */
  bodyClassName?: string;
  /** Refetching: the card keeps its figures, dimmed, instead of flashing a skeleton. */
  busy?: boolean;
}) {
  return (
    <section className={`card flex min-w-0 flex-col ${className}`} aria-busy={busy || undefined}>
      <header className="flex flex-wrap items-start justify-between gap-x-3 gap-y-2 border-b border-line px-4 py-3 sm:px-5">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold">{title}</h3>
          {subtitle && <p className="mt-0.5 text-xs text-ink-muted">{subtitle}</p>}
        </div>
        {action}
      </header>
      <div className={`min-w-0 flex-1 p-4 transition-opacity sm:p-5 ${busy ? "opacity-60" : ""} ${bodyClassName}`}>{children}</div>
    </section>
  );
}

/** One choice of a few, as a row of buttons (an ARIA radio group: arrow keys move the choice). */
export function Segmented<T extends string | number>({
  label,
  options,
  value,
  onChange,
  size = "md",
}: {
  label: string;
  options: readonly { value: T; label: string }[];
  value: T;
  onChange: (value: T) => void;
  size?: "sm" | "md";
}) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const index = Math.max(0, options.findIndex((o) => o.value === value));
  const move = (e: KeyboardEvent, i: number) => {
    const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[e.key];
    if (!step) return;
    e.preventDefault();
    const next = (i + step + options.length) % options.length;
    onChange(options[next].value);
    refs.current[next]?.focus();
  };
  return (
    <div role="radiogroup" aria-label={label} className="inline-flex max-w-full overflow-x-auto rounded-xl border border-line bg-surface p-0.5 shadow-xs">
      {options.map((o, i) => {
        const on = i === index;
        return (
          <button
            key={String(o.value)}
            ref={(el) => {
              refs.current[i] = el;
            }}
            type="button"
            role="radio"
            aria-checked={on}
            tabIndex={on ? 0 : -1}
            onClick={() => onChange(o.value)}
            onKeyDown={(e) => move(e, i)}
            className={`shrink-0 whitespace-nowrap rounded-lg font-medium transition ${size === "sm" ? "px-2.5 py-1 text-xs" : "px-3 py-1.5 text-sm"} ${
              on ? "bg-brand text-brand-fg shadow-brand" : "text-ink-soft hover:bg-brand-50 hover:text-brand"
            }`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

const TONE_TEXT = { good: "text-success", bad: "text-danger", neutral: "text-ink-muted" } as const;

/** How a figure moved since the previous period: arrow + "+12%", toned by whether up is good. */
export function ChangeBadge({
  change,
  upIsGood = true,
  vs,
  label,
}: {
  change: Change;
  upIsGood?: boolean;
  /** The comparison period, e.g. "the previous 30 days". */
  vs: string;
  /** Overrides the "+12%" text, e.g. "+4 pts" for a rate. */
  label?: string;
}) {
  const text = label ?? changeLabel(change);
  const t = tone(change.trend, upIsGood);
  const spoken = change.trend === "flat" ? `No change from ${vs}` : `${change.trend === "up" ? "Up" : "Down"} ${text.replace(/^[+−]/, "")} from ${vs}`;
  return (
    <span className="mt-1.5 flex flex-wrap items-center gap-x-1.5 text-xs">
      <span className={`inline-flex items-center gap-0.5 font-semibold ${TONE_TEXT[t]}`} aria-hidden>
        <TrendArrow trend={change.trend} />
        {text}
      </span>
      <span className="text-ink-muted" aria-hidden>
        vs {vs.replace(/^the /, "")}
      </span>
      <span className="sr-only">{spoken}</span>
    </span>
  );
}

function TrendArrow({ trend }: { trend: Change["trend"] }) {
  if (trend === "flat") return <span className="px-0.5">–</span>;
  return (
    <svg viewBox="0 0 12 12" className={`h-3 w-3 ${trend === "down" ? "rotate-180" : ""}`} aria-hidden>
      <path d="M6 2.5 10 8H2Z" fill="currentColor" />
    </svg>
  );
}

/** A headline figure. `hero`: the one number the dashboard leads with (spans two columns). */
export function StatTile({
  label,
  value,
  exact,
  hero = false,
  children,
}: {
  label: string;
  value: string;
  /** The exact figure behind a compact one, as a tooltip ("₹12,40,512.00"). */
  exact?: string;
  hero?: boolean;
  /** Below the value: a ChangeBadge and/or a short note. */
  children?: ReactNode;
}) {
  return (
    <div className={`card min-w-0 p-4 ${hero ? "col-span-2 bg-linear-to-br from-surface to-brand-50" : ""}`}>
      <div className="truncate text-xs font-medium text-ink-muted">{label}</div>
      <div
        className={`mt-1 truncate font-semibold tracking-tight text-ink ${hero ? "text-4xl sm:text-5xl" : "text-xl sm:text-2xl"}`}
        title={exact}
      >
        {value}
      </div>
      {children}
    </div>
  );
}

/** A rank's move since the previous period: "▲ 3", "▼ 2", "New" or "–". */
export function RankMoveBadge({ rank, previous }: { rank: number; previous: number | null }) {
  const m = rankMove(rank, previous);
  if (m.kind === "new") return <span className="badge-info px-1.5 py-0 text-[10px]" title="Not sold in the previous period">New</span>;
  if (m.kind === "same")
    return (
      <span className="text-xs text-ink-muted" title="Same rank as the previous period">
        –<span className="sr-only">no change</span>
      </span>
    );
  const up = m.kind === "up";
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-semibold tabular-nums ${up ? "text-success" : "text-danger"}`} title={`Was #${previous}`}>
      <TrendArrow trend={up ? "up" : "down"} />
      {m.by}
      <span className="sr-only">{up ? " places up" : " places down"}, was {previous}</span>
    </span>
  );
}

/** A thin bar for a share (0–1), on a lighter track of the same hue. */
export function ShareBar({ fraction, label }: { fraction: number; label?: string }) {
  const pct = Math.max(0, Math.min(1, fraction)) * 100;
  return (
    <div className="h-1.5 w-full rounded-full bg-brand-50" role={label ? "img" : undefined} aria-label={label}>
      <div className="h-1.5 rounded-r-full bg-brand-500" style={{ width: `${pct}%`, minWidth: pct > 0 ? 2 : 0 }} />
    </div>
  );
}

export function Skeleton({ className = "" }: { className?: string }) {
  return <div className={`skeleton ${className}`} aria-hidden />;
}

export function LoadError({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-danger/30 bg-danger-soft px-3 py-2 text-sm text-danger" role="alert">
      <span>{message}</span>
      <button type="button" onClick={onRetry} className="btn-secondary btn-sm">
        Try again
      </button>
    </div>
  );
}

export function WarningIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="h-3.5 w-3.5 shrink-0" aria-hidden>
      <path d="M12 3 2 20h20L12 3Z" />
      <path d="M12 10v4M12 17h.01" />
    </svg>
  );
}
